import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  CloudUpload,
  FileArchive,
  Pencil,
  RefreshCw,
  RotateCcw,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { Layout } from '../components/Layout.tsx';
import { appUpdatesAPI } from '../services/api.ts';
import {
  ConfirmModal,
  EmptyState,
  PageHeader,
} from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';

// Matches the limit the backend enforces when the release is confirmed.
const MAX_APK_BYTES = 300 * 1024 * 1024;

const errorOf = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const formatBytes = (bytes?: number) =>
  bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '—';

/**
 * Releases of the Android app. Uploading an APK here and making it current
 * is what makes installed apps offer the update; making an older release
 * current again is the rollback.
 */
export const AppUpdates = () => {
  const [releases, setReleases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [file, setFile] = useState<File | null>(null);
  const [versionCode, setVersionCode] = useState('');
  const [versionName, setVersionName] = useState('');
  const [notes, setNotes] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [currentTarget, setCurrentTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editMandatory, setEditMandatory] = useState(false);
  const [working, setWorking] = useState(false);

  const load = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      const response = await appUpdatesAPI.list();
      setReleases(response.data || []);
      setError('');
    } catch (err: any) {
      setError(errorOf(err, 'Failed to load releases'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Leaving mid-upload abandons it; make the browser ask first.
  useEffect(() => {
    if (!uploading) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  const resetForm = () => {
    setFile(null);
    setVersionCode('');
    setVersionName('');
    setNotes('');
    setMandatory(false);
    setProgress(0);
  };

  const publish = async () => {
    const code = Number(versionCode);
    if (!Number.isInteger(code) || code <= 0) {
      toast.error('Version code must be a positive whole number.');
      return;
    }
    if (!versionName.trim()) {
      toast.error('Version name is required.');
      return;
    }
    if (!file) {
      toast.error('Select an APK file to upload.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.apk')) {
      toast.error('The selected file must be an .apk.');
      return;
    }
    if (file.size > MAX_APK_BYTES) {
      toast.error('The APK is larger than 300 MB.');
      return;
    }

    try {
      setUploading(true);
      setProgress(0);

      // 1. Where to send the file. 2. Send it straight to storage.
      // 3. Tell the API it is there, which creates the release.
      const target = await appUpdatesAPI.getUploadUrl({
        version_code: code,
        version_name: versionName.trim(),
        file_name: file.name,
      });
      await appUpdatesAPI.uploadApk(
        target.upload_url,
        target.content_type,
        file,
        setProgress,
      );
      const response = await appUpdatesAPI.create({
        version_code: code,
        version_name: versionName.trim(),
        file_path: target.file_path,
        notes: notes.trim(),
        mandatory,
      });

      toast.success(
        response.message ||
          `Version ${versionName.trim()} published and set as current.`,
        { duration: 8000 },
      );
      resetForm();
      await load(true);
    } catch (err: any) {
      toast.error(errorOf(err, 'Failed to publish the release.'));
    } finally {
      setUploading(false);
    }
  };

  const makeCurrent = async () => {
    try {
      setWorking(true);
      await appUpdatesAPI.makeCurrent(currentTarget._id);
      toast.success(`Version ${currentTarget.version_name} is now current.`);
      setCurrentTarget(null);
      await load(true);
    } catch (err: any) {
      toast.error(errorOf(err, 'Failed to set the current release.'));
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    try {
      setWorking(true);
      await appUpdatesAPI.remove(deleteTarget._id);
      toast.success(`Version ${deleteTarget.version_name} deleted.`);
      setDeleteTarget(null);
      await load(true);
    } catch (err: any) {
      toast.error(errorOf(err, 'Failed to delete the release.'));
    } finally {
      setWorking(false);
    }
  };

  const openEdit = (release: any) => {
    setEditNotes(release.notes || '');
    setEditMandatory(!!release.mandatory);
    setEditTarget(release);
  };

  const saveEdit = async () => {
    try {
      setWorking(true);
      await appUpdatesAPI.update(editTarget._id, {
        notes: editNotes.trim(),
        mandatory: editMandatory,
      });
      toast.success(`Version ${editTarget.version_name} updated.`);
      setEditTarget(null);
      await load(true);
    } catch (err: any) {
      toast.error(errorOf(err, 'Failed to update the release.'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="App Updates"
        description="Over-the-air APK releases for the TeamSol Android app."
        actions={
          <Button
            variant="default"
            leftSection={<RefreshCw size={16} />}
            onClick={() => load()}
          >
            Refresh
          </Button>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }} mb="lg">
        <Stack gap="md" maw={640}>
          <Group gap="xs">
            <CloudUpload size={20} />
            <Title order={4}>Publish new version</Title>
          </Group>

          <FileInput
            label="APK file"
            placeholder="Select the .apk"
            accept=".apk,application/vnd.android.package-archive"
            leftSection={<FileArchive size={16} />}
            value={file}
            onChange={setFile}
            disabled={uploading}
            clearable
            description={
              file ? `${file.name} · ${formatBytes(file.size)}` : undefined
            }
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Version code"
              placeholder="e.g. 42"
              inputMode="numeric"
              description="Android versionCode. Must be higher than the build it replaces."
              value={versionCode}
              onChange={(event) =>
                setVersionCode(event.currentTarget.value.replace(/[^0-9]/g, ''))
              }
              disabled={uploading}
            />
            <TextInput
              label="Version name"
              placeholder="e.g. 1.4.2"
              description="Shown in the in-app update popup."
              value={versionName}
              onChange={(event) => setVersionName(event.currentTarget.value)}
              disabled={uploading}
            />
          </SimpleGrid>

          <Textarea
            label="Release notes (optional)"
            placeholder="What changed in this build…"
            autosize
            minRows={3}
            maxRows={8}
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
            disabled={uploading}
          />

          <Switch
            label="Mandatory update"
            description="Users cannot skip a mandatory update."
            checked={mandatory}
            onChange={(event) => setMandatory(event.currentTarget.checked)}
            disabled={uploading}
          />

          {uploading && (
            <Stack gap={6}>
              <Progress value={progress} animated={progress >= 100} />
              <Text size="xs" c="dimmed">
                {progress < 100
                  ? `Uploading APK… ${progress}%. Keep this page open.`
                  : 'Finalizing release…'}
              </Text>
            </Stack>
          )}

          <Group>
            <Button
              leftSection={<CloudUpload size={16} />}
              onClick={publish}
              loading={uploading}
            >
              Publish &amp; set current
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Title order={4}>Releases</Title>

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : releases.length === 0 ? (
            !error && (
              <EmptyState
                icon={<Smartphone size={28} />}
                title="No releases yet"
                description="Publish the first APK above to make it available to the app."
              />
            )
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Version</Table.Th>
                    <Table.Th>Size</Table.Th>
                    <Table.Th>Uploaded</Table.Th>
                    <Table.Th>Notes</Table.Th>
                    <Table.Th>Mandatory</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {releases.map((release) => (
                    <Table.Tr key={release._id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" fw={600}>
                            {release.version_name}
                          </Text>
                          {release.is_current && (
                            <Badge color="green" variant="light">
                              Current
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          code {release.version_code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatBytes(release.file_size)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {dayjs(release.createdAt).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </Table.Td>
                      <Table.Td maw={360}>
                        <Text
                          size="sm"
                          c={release.notes ? undefined : 'dimmed'}
                          lineClamp={3}
                          style={{ whiteSpace: 'pre-line' }}
                        >
                          {release.notes || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {release.mandatory ? (
                          <Badge color="red" variant="light">
                            Mandatory
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">
                            Optional
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          {!release.is_current && (
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<RotateCcw size={12} />}
                              onClick={() => setCurrentTarget(release)}
                            >
                              Make current
                            </Button>
                          )}
                          <Button
                            size="compact-xs"
                            variant="default"
                            leftSection={<Pencil size={12} />}
                            onClick={() => openEdit(release)}
                          >
                            Edit notes
                          </Button>
                          {!release.is_current && (
                            <Button
                              size="compact-xs"
                              variant="light"
                              color="red"
                              leftSection={<Trash2 size={12} />}
                              onClick={() => setDeleteTarget(release)}
                            >
                              Delete
                            </Button>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>

      <ConfirmModal
        opened={!!currentTarget}
        onClose={() => setCurrentTarget(null)}
        onConfirm={makeCurrent}
        title={`Make version ${currentTarget?.version_name} current?`}
        confirmLabel="Make current"
        color="green"
        loading={working}
      >
        Users whose build is older than version code{' '}
        {currentTarget?.version_code} will be prompted to install it. Android
        will not downgrade phones already on a higher version code.
      </ConfirmModal>

      <ConfirmModal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        title={`Delete version ${deleteTarget?.version_name}?`}
        confirmLabel="Delete"
        loading={working}
      >
        This permanently removes the release and its APK file. This cannot be
        undone.
      </ConfirmModal>

      <Modal
        opened={!!editTarget}
        onClose={working ? () => {} : () => setEditTarget(null)}
        title={<Text fw={600}>Edit version {editTarget?.version_name}</Text>}
      >
        <Stack gap="md">
          <Textarea
            label="Release notes"
            placeholder="What changed in this build…"
            autosize
            minRows={4}
            maxRows={10}
            value={editNotes}
            onChange={(event) => setEditNotes(event.currentTarget.value)}
            disabled={working}
          />
          <Switch
            label="Mandatory update"
            description="Users cannot skip a mandatory update."
            checked={editMandatory}
            onChange={(event) => setEditMandatory(event.currentTarget.checked)}
            disabled={working}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setEditTarget(null)}
              disabled={working}
            >
              Cancel
            </Button>
            <Button onClick={saveEdit} loading={working}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Layout>
  );
};

export default AppUpdates;
